import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../../shared/StyledInput.css';
import { carrierChoices, servicesForCarrier, carrierForService } from '../../shared/shippingCarriage';

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

// An order's own Carrier/Service/Options (any order type), as the starting
// point for a new shipment (issue #281).
export function carriageFromOrder(order) {
  const service = order?.shipping_service;
  return {
    carrier_id: order?.carrier_id ? String(order.carrier_id) : '',
    shipping_service_id: service ? String(service.id) : '',
    shipping_option_ids: service ? (order.selected_options || []).map((o) => String(o.shipping_option_id)) : [],
  };
}

export function carriageFromShipment(row) {
  return {
    carrier_id: row?.carrier_id ? String(row.carrier_id) : '',
    shipping_service_id: row?.shipping_service_id ? String(row.shipping_service_id) : '',
    shipping_option_ids: (row?.shipping_options || []).map((o) => String(o.shipping_option_id)),
  };
}

export function carriagePayload(value) {
  return {
    carrier_id: value.carrier_id || null,
    shipping_service_id: value.shipping_service_id || null,
    shipping_option_ids: value.shipping_option_ids || [],
  };
}

// One line for a list cell — "Carrier · Service", Options beneath.
export function carriageLabel(row) {
  return [row.carrier?.name, row.shipping_service?.description].filter(Boolean).join(' · ');
}

// Carrier, Service and Options for one shipment (issue #281), shared by
// every Ship entry point (via ShipConfirmFields.js) and ShipmentsTable.js's
// Edit drawer — same Carrier → Service → Options order as Customer, Quote
// and Sales Order (lists from shared/shippingCarriage.js). Changing the
// Carrier clears the Service and Options; changing the Service clears the
// Options. Inactive Options stay listed only while already selected, so
// editing an older shipment doesn't silently drop them. No Option costs
// here — a shipment never changes what the Customer is charged. All
// optional — nothing here blocks Ship.
function ShipmentCarriageFields({ value, onChange }) {
  const [services, setServices] = useState([]);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    axios.get('/api/shipping-services', { params: { status: 'all' } })
      .then((res) => setServices(res.data.data || []))
      .catch(() => setServices([]));
  }, []);

  useEffect(() => {
    if (!value.shipping_service_id) { setOptions([]); return; }
    axios.get('/api/shipping-options', { params: { shipping_service_id: value.shipping_service_id, status: 'all' } })
      .then((res) => setOptions(res.data.data || []))
      .catch(() => setOptions([]));
  }, [value.shipping_service_id]);

  const carriers = carrierChoices(services, value.carrier_id);
  const carrierServices = servicesForCarrier(services, value.carrier_id, value.shipping_service_id);
  const optionsForService = options.filter((o) => o.is_active || value.shipping_option_ids.includes(String(o.id)));

  const setCarrier = (carrierId) => onChange({ carrier_id: carrierId, shipping_service_id: '', shipping_option_ids: [] });
  const setService = (serviceId) => onChange({ ...value, carrier_id: carrierForService(services, serviceId, value.carrier_id), shipping_service_id: serviceId, shipping_option_ids: [] });
  const toggleOption = (optionId, checked) => onChange({
    ...value,
    shipping_option_ids: checked
      ? [...value.shipping_option_ids, optionId]
      : value.shipping_option_ids.filter((id) => id !== optionId),
  });

  return (
    <>
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Carrier</label>
        <select className="styled-input" value={value.carrier_id} onChange={(e) => setCarrier(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
          <option value="">(none)</option>
          {carriers.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
      </div>
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Service</label>
        <select className="styled-input" value={value.shipping_service_id} onChange={(e) => setService(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
          <option value="">(none)</option>
          {carrierServices.map((sv) => <option key={sv.id} value={String(sv.id)}>{sv.description}</option>)}
        </select>
      </div>
      {optionsForService.length > 0 && (
        <div style={fieldWrapStyle}>
          <label style={fieldLabelStyle}>Options</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {optionsForService.map((o) => (
              <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={value.shipping_option_ids.includes(String(o.id))}
                  onChange={(e) => toggleOption(String(o.id), e.target.checked)}
                />
                {o.description}
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default ShipmentCarriageFields;
